import type { HarmonicFieldFrame } from './HarmonicField'

export type PerceptualQuality = 'low' | 'balanced' | 'high' | 'ultra'

export interface PerceptualMorphogenesisInput {
  salience01: number
  motion01: number
  occlusion01: number
  distance01: number
  contrast01: number
  exposure01: number
  surfaceComplexity01: number
  runtimePressure01: number
  reducedMotion?: boolean
}

export interface PerceptualMorphogenesisFrame {
  materialRoughnessDelta: number
  materialMetalnessDelta: number
  emissivePulse: number
  iridescenceShift: number
  normalEnergy: number
  edgeLuminance: number
  chromaMicroShift: number
  vfxDensity: number
  particlePersistence: number
  lightPulse: number
  silhouetteBreath: number
  cameraMicroDrift: number
  temporalDither: number
  detailBudget01: number
  shaderBudget01: number
  lodBias: number
  visualEnergy01: number
  signature: string
}

export interface PerceptualMorphogenesisConfig {
  identity: string
  quality: PerceptualQuality
  maxVisualEnergy: number
}

const PHI = 1.618033988749895
const SQRT2 = 1.4142135623730951
const SQRT3 = 1.7320508075688772
const TAU = Math.PI * 2

const clamp = (n: number, lo = 0, hi = 1) =>
  Math.max(lo, Math.min(hi, Number.isFinite(n) ? n : lo))
const signed = (n: number) => Math.max(-1, Math.min(1, Number.isFinite(n) ? n : 0))
const fract = (n: number) => n - Math.floor(n)

function hash32(input: string): number {
  let h = 2166136261 >>> 0
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  h ^= h >>> 16
  h = Math.imul(h, 0x7feb352d)
  h ^= h >>> 15
  return h >>> 0
}

function unit(seed: number, channel: number): number {
  let x = (seed ^ Math.imul(channel + 1, 0x9e3779b9)) >>> 0
  x ^= x >>> 16
  x = Math.imul(x, 0x7feb352d)
  x ^= x >>> 15
  x = Math.imul(x, 0x846ca68b)
  x ^= x >>> 16
  return (x >>> 0) / 4294967295
}

function quant(n: number): string {
  return Math.round(n * 10000).toString(36)
}const QUALITY_GAIN: Record<PerceptualQuality, number> = {
  low: 0.56,
  balanced: 0.74,
  high: 0.9,
  ultra: 1,
}

export class PerceptualMorphogenesisFieldRuntime {
  readonly config: PerceptualMorphogenesisConfig
  private readonly seed: number

  constructor(identity: string, quality: PerceptualQuality = 'high') {
    this.config = { identity, quality, maxVisualEnergy: 1 }
    this.seed = hash32(identity || 'inyron-perceptual')
  }

  sample(
    timeS: number,
    input: Partial<PerceptualMorphogenesisInput> = {},
    harmonic?: HarmonicFieldFrame,
  ): PerceptualMorphogenesisFrame {
    const t = Math.max(0, Number.isFinite(timeS) ? timeS : 0)
    const salience = clamp(input.salience01 ?? 0.5)
    const motion = clamp(input.motion01 ?? 0.2)
    const occlusion = clamp(input.occlusion01 ?? 0)
    const distance = clamp(input.distance01 ?? 0)
    const contrast = clamp(input.contrast01 ?? 0.45)
    const exposure = clamp(input.exposure01 ?? 0.5)
    const surface = clamp(input.surfaceComplexity01 ?? 0.5)
    const pressure = clamp(input.runtimePressure01 ?? 0)
    const reducedMotion = Boolean(input.reducedMotion)

    const visibility = clamp((1 - occlusion * 0.86) * (1 - distance * 0.72))
    const perceptualNeed = clamp(
      salience * 0.48 + contrast * 0.2 + motion * 0.18 + surface * 0.14,
    )
    const quality = QUALITY_GAIN[this.config.quality]
    const visualEnergy01 = clamp(
      perceptualNeed * visibility * quality * (1 - pressure * 0.72),
      0,
      this.config.maxVisualEnergy,
    )

    // Hyperphase lattice: irrationally-related temporal carriers prevent short visible loops.
    const p0 = unit(this.seed, 3) * TAU
    const p1 = unit(this.seed, 7) * TAU
    const p2 = unit(this.seed, 13) * TAU
    const hyperA = Math.sin(t * PHI * 1.73 + p0)
    const hyperB = Math.sin(t * SQRT2 * 2.11 + p1)
    const hyperC = Math.sin(t * SQRT3 * 0.91 + p2)
    const hyperphase = signed(hyperA * 0.46 + hyperB * 0.34 + hyperC * 0.2)

    const hLow = harmonic?.bands.low ?? 0
    const hHigh = harmonic?.bands.high ?? 0
    const hUltra = harmonic?.bands.ultra ?? 0
    const hInter = harmonic?.intermodulation ?? 0    // Golden-discrepancy temporal dither: deterministic, low-repetition, replay-safe.
    const epoch = Math.floor(t * 60)
    const temporalDither = fract(epoch / PHI + unit(this.seed, 29)) * 2 - 1

    // Perceptual energy conservation: expensive channels only grow when the moment is visible/salient.
    const materialEnergy = visualEnergy01 * (0.44 + surface * 0.32)
    const lightEnergy = visualEnergy01 * (0.26 + contrast * 0.22 + (1 - exposure) * 0.14)
    const motionEnergy = visualEnergy01 * (0.24 + motion * 0.3)
    const fxEnergy = visualEnergy01 * (0.3 + salience * 0.42)

    const detailBudget01 = clamp(visualEnergy01 * (0.66 + surface * 0.34))
    const shaderBudget01 = clamp(detailBudget01 * (1 - pressure * 0.58))
    const lodBias = clamp(distance * 1.45 + pressure * 1.15 - salience * 0.62, -0.5, 2)

    const motionSuppression = reducedMotion ? 0.18 : 1
    const materialRoughnessDelta = signed((-hyperphase * 0.028 + hLow * 0.014) * materialEnergy)
    const materialMetalnessDelta = signed((hHigh * 0.018 + hInter * 0.012) * materialEnergy)
    const emissivePulse = clamp((0.24 + Math.abs(hHigh) * 0.38 + Math.abs(hUltra) * 0.22) * lightEnergy)
    const iridescenceShift = signed((hyperB * 0.42 + hInter * 0.34 + temporalDither * 0.08) * materialEnergy)
    const normalEnergy = clamp((0.56 + Math.abs(hLow) * 0.22 + surface * 0.22) * materialEnergy)
    const edgeLuminance = clamp((0.34 + contrast * 0.34 + Math.abs(hHigh) * 0.24) * lightEnergy)
    const chromaMicroShift = signed((hyperC * 0.0035 + hUltra * 0.0025) * lightEnergy)
    const vfxDensity = clamp((0.42 + Math.abs(harmonic?.vfxEmission ?? hyperphase) * 0.44) * fxEnergy)
    const particlePersistence = clamp((0.48 + salience * 0.32 - motion * 0.12) * fxEnergy)
    const lightPulse = signed((hyperA * 0.42 + hHigh * 0.34 + hInter * 0.24) * lightEnergy)
    const silhouetteBreath = signed((hyperphase * 0.006 + hLow * 0.004) * motionEnergy) * motionSuppression
    const cameraMicroDrift = signed((hyperC * 0.003 + hInter * 0.002) * motionEnergy) * motionSuppression

    const signature = [
      this.seed.toString(36),
      quant(visualEnergy01),
      quant(detailBudget01),
      quant(shaderBudget01),
      quant(lodBias),
      quant(materialRoughnessDelta),
      quant(emissivePulse),
      quant(iridescenceShift),
      quant(vfxDensity),
      quant(lightPulse),
      quant(temporalDither),
      harmonic?.signature ?? 'no-harmonic',
    ].join(':')

    return {
      materialRoughnessDelta,
      materialMetalnessDelta,
      emissivePulse,
      iridescenceShift,
      normalEnergy,
      edgeLuminance,      chromaMicroShift,
      vfxDensity,
      particlePersistence,
      lightPulse,
      silhouetteBreath,
      cameraMicroDrift,
      temporalDither,
      detailBudget01,
      shaderBudget01,
      lodBias,
      visualEnergy01,
      signature,
    }
  }
}
